import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import React, { useEffect, useState } from 'react';
import { Input, message, Popconfirm, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import ActiveCodeFormModal from './ActiveCodeFormModal';
import { ModePage } from 'common/enum';
import processError from '../../../../utils/processError';
import { activeCodeStatusList, getEnumLabel } from '../../../../utils/enumMapper';
import { ActiveCode, Book } from '../../../../model/response';
import { formatDate, mapToSelectItems } from '../../../../utils/utils';
import { findAllBook } from '../../../../service/bookService';
import { useMasterSubjectsSelector } from '../../../../redux/selector';
import useTableConfig from '../../../../hooks/useTableConfig';

export default function ActiveCodeManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const [keyword, setKeyword] = useState<string>('');
  const [activeCodeId, setActiveCodeId] = useState<string>('');
  const [mode, setMode] = useState<ModePage>(ModePage.ADD);
  const [isOpen, setOpenModal] = useState<boolean>(false);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const subjects = useMasterSubjectsSelector();
  const [bookId, setBookId] = useState<string | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [data, setData] = useState<ActiveCode[]>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBookId(null);
    findAllBook(subjectId).then(setBooks).catch(processError);
  }, [subjectId]);

  const columns: ColumnsType<ActiveCode> = [
    {
      title: 'Mã kích hoạt',
      dataIndex: 'code',
      align: 'left',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Sách',
      dataIndex: 'book.name',
      align: 'left',
      render: (_, record) => record.book?.name,
      sorter: true,
      sortOrder: getColumnSorter('book.name'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      align: 'center',
      render: (_, record) => getEnumLabel(record.status, activeCodeStatusList),
      sorter: true,
      sortOrder: getColumnSorter('status'),
    },
    {
      title: 'Ngày kích hoạt',
      dataIndex: 'activeDate',
      render: (_, record) => (record.activeDate ? formatDate(new Date(record.activeDate)) : ''),
      align: 'left',
      sorter: true,
      sortOrder: getColumnSorter('activeDate'),
    },
    {
      title: 'Id người kích hoạt',
      dataIndex: 'userId',
      align: 'left',
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      align: 'left',
      sorter: true,
      sortOrder: getColumnSorter('serialNumber'),
    },
    {
      title: 'ISBN',
      dataIndex: 'isbn',
      align: 'left',
      sorter: true,
      sortOrder: getColumnSorter('isbn'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa Mã kích hoạt"
            description="Xác nhận xóa Mã kích hoạt. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteActiveCode(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteActiveCode = async (record: ActiveCode) => {
    setLoading(true);
    try {
      await axiosInstance({
        url: `active-codes/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công.');
      await loadListActiveCodes();
    } catch (err) {
      processError(err);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: ActiveCode) => {
    setMode(ModePage.EDIT);
    setOpenModal(true);
    setActiveCodeId(record.id);
  };

  const loadListActiveCodes = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: 'active-codes/page',
      params: {
        keyword,
        sortBy,
        direction,
        size: pageSize,
        page: currentPage,
        subjectId,
        bookId,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewActiveCode = async () => {
    setMode(ModePage.ADD);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBeforeSuccess = () => {
    setOpenModal(false);
    loadListActiveCodes().then();
  };

  useEffect(() => {
    loadListActiveCodes().then();
  }, [currentPage, pageSize, sortBy, direction, keyword, subjectId, bookId]);

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewActiveCode()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <Select
            onChange={(e) => setSubjectId(e)}
            className="w-full min-w-[240px]"
            placeholder="Bộ lọc - Môn học"
            style={{ flex: 1 }}
            value={subjectId}
            options={[
              {
                value: '',
                label: 'Tất cả',
              },
            ].concat(mapToSelectItems(subjects))}
          />
          <Select
            onChange={(e) => setBookId(e)}
            className="w-full min-w-[240px]"
            placeholder="Bộ lọc - Sách"
            style={{ flex: 1 }}
            value={bookId}
            options={[
              {
                value: '',
                label: 'Tất cả',
              },
            ].concat(mapToSelectItems(books))}
          />
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <ActiveCodeFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        activeCodeId={activeCodeId}
      />
    </div>
  );
}
